import type { CouponRedemption } from "@kuroo/contracts";
import type { Coupon, CouponDetails, CouponTarget } from "./client-catalog-types";

export type SdkCouponsContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function applyCoupon(context: SdkCouponsContext, bookingId: string, code: string): Promise<{ bookingId: string; couponId: string; discountCents: number; totalCents?: number }> {
  return context.request<{ bookingId: string; couponId: string; discountCents: number; totalCents?: number }>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/coupon`, { method: "POST", body: JSON.stringify({ code }) });
}

export function validateCoupon(context: SdkCouponsContext, code: string, subtotalCents: number, currency = "EUR", scope?: { entityType: CouponTarget["entityType"]; entityIds: string[] }): Promise<{ code: string; discountCents: number; totalCents: number; currency: string; targets?: CouponTarget[] }> {
  return context.request<{ code: string; discountCents: number; totalCents: number; currency: string; targets?: CouponTarget[] }>("/api/v1/coupons/validate", { method: "POST", body: JSON.stringify({ code, subtotalCents, currency, ...(scope ?? {}) }) });
}

export function coupons(context: SdkCouponsContext): Promise<Coupon[]> {
  return context.request<Coupon[]>("/api/v1/coupons");
}

export function createCoupon(context: SdkCouponsContext, input: { code: string; kind: "percentage" | "fixed"; amount: number; currency?: string; maxRedemptions?: number; validFrom?: string | null; validUntil?: string | null; status?: "active" | "inactive"; targets?: CouponTarget[] }): Promise<{ id: string; code: string; kind: string; amount: number; targets: CouponTarget[] }> {
  return context.request<{ id: string; code: string; kind: string; amount: number; targets: CouponTarget[] }>("/api/v1/coupons", { method: "POST", body: JSON.stringify(input) });
}

export function coupon(context: SdkCouponsContext, couponId: string): Promise<CouponDetails> {
  return context.request<CouponDetails>(`/api/v1/coupons/${encodeURIComponent(couponId)}`);
}

export function updateCoupon(context: SdkCouponsContext, couponId: string, input: { code?: string; kind?: "percentage" | "fixed"; amount?: number; currency?: string | null; maxRedemptions?: number | null; validFrom?: string | null; validUntil?: string | null; status?: "active" | "inactive"; targets?: CouponTarget[] | null }): Promise<CouponDetails> {
  return context.request<CouponDetails>(`/api/v1/coupons/${encodeURIComponent(couponId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function updateCouponStatus(context: SdkCouponsContext, couponId: string, status: "active" | "inactive"): Promise<{ id: string; status: "active" | "inactive" }> {
  return context.request<{ id: string; status: "active" | "inactive" }>(`/api/v1/coupons/${encodeURIComponent(couponId)}/status`, { method: "POST", body: JSON.stringify({ status }) });
}

export function deleteCoupon(context: SdkCouponsContext, couponId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
  return context.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/coupons/${encodeURIComponent(couponId)}`, { method: "DELETE" });
}

export function couponRedemptions(context: SdkCouponsContext, couponId: string, filters: { status?: string; from?: string; to?: string } = {}): Promise<CouponRedemption[]> {
  const query = new URLSearchParams();
  if (filters.status) query.set("status", filters.status);
  if (filters.from) query.set("from", filters.from);
  if (filters.to) query.set("to", filters.to);
  return context.request<CouponRedemption[]>(`/api/v1/coupons/${encodeURIComponent(couponId)}/redemptions${query.size ? `?${query}` : ""}`);
}
